import React, { useState } from 'react';
import { Shield, Phone, Key, ArrowLeft, CheckCircle, AlertCircle, X } from 'lucide-react';

function EnrollMFA() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [mfaStep, setMfaStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Mock functions - replace with your actual Firebase functions
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock verification ID
      setVerificationId('mock-verification-id');
      setMfaStep('otp');
      setSuccess('OTP sent successfully to your phone!');
    } catch (error) {
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (otp === '123456') {
        setMfaStep('success');
        setSuccess('MFA enrolled successfully!');
      } else {
        throw new Error('Invalid OTP. Please try again.');
      }
    } catch (error) {
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPhoneNumber('');
    setOtp('');
    setVerificationId('');
    setMfaStep('phone');
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Multi-Factor Authentication</h1>
          <p className="text-gray-600">Secure your account with phone verification</p>
        </div>

        {/* MFA Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                mfaStep === 'phone' ? 'bg-blue-600 text-white' : 
                mfaStep === 'otp' || mfaStep === 'success' ? 'bg-green-600 text-white' : 'bg-gray-300'
              }`}>
                <Phone className="w-4 h-4" />
              </div>
              <div className={`w-12 h-1 mx-2 ${
                mfaStep === 'otp' || mfaStep === 'success' ? 'bg-green-600' : 'bg-gray-300'
              }`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                mfaStep === 'otp' ? 'bg-blue-600 text-white' : 
                mfaStep === 'success' ? 'bg-green-600 text-white' : 'bg-gray-300'
              }`}>
                <Key className="w-4 h-4" />
              </div>
              <div className={`w-12 h-1 mx-2 ${
                mfaStep === 'success' ? 'bg-green-600' : 'bg-gray-300'
              }`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                mfaStep === 'success' ? 'bg-green-600 text-white' : 'bg-gray-300'
              }`}>
                <CheckCircle className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700 flex-1">{error}</span>
              <button 
                onClick={() => setError('')} 
                className="text-red-500 hover:text-red-700 ml-2"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-green-700 flex-1">{success}</span>
              <button 
                onClick={() => setSuccess('')} 
                className="text-green-500 hover:text-green-700 ml-2"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Phone Number Step */}
          {mfaStep === 'phone' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Enter Your Phone Number</h3>
              <div onSubmit={handlePhoneSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="+1 (555) 123-4567"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    We'll send a verification code to this number
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handlePhoneSubmit}
                  disabled={loading || !phoneNumber}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending OTP...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Phone className="w-5 h-5 mr-2" />
                      Send Verification Code
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* OTP Verification Step */}
          {mfaStep === 'otp' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Enter Verification Code</h3>
              <div onSubmit={handleOtpSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    6-Digit Code
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-center text-lg font-mono tracking-widest"
                    placeholder="123456"
                    maxLength="6"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Enter the code sent to {phoneNumber}
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setMfaStep('phone')}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleOtpSubmit}
                    disabled={loading || otp.length !== 6}
                    className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Verifying...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Key className="w-4 h-4 mr-2" />
                        Verify Code
                      </div>
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => setMfaStep('phone')}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Didn't receive the code? Try again
                </button>
              </div>
            </div>
          )}

          {/* Success Step */}
          {mfaStep === 'success' && (
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">MFA Successfully Enrolled!</h3>
              <p className="text-gray-600 mb-6">
                Your account is now protected with multi-factor authentication.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.history.back()}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Return to Dashboard
                </button>
                <button
                  onClick={resetForm}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Enroll Another Device
                </button>
              </div>
            </div>
          )}

          {/* Demo Note */}
          {mfaStep === 'otp' && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700 text-center">
                <strong>Demo:</strong> Use code "123456" to complete verification
              </p>
            </div>
          )}
        </div>

        {/* reCAPTCHA Container */}
        <div id="recaptcha-container" className="mt-4"></div>
      </div>
    </div>
  );
}

export default EnrollMFA;